/**
 * The one check-in OTP every candidate under a TEST organisation is issued (0041).
 *
 * A KNOWN constant, never a revealed secret. candidate_otps still stores only the sha256, the row
 * still expires, is still single-use and still attempt-capped, and inviteCheckin still verifies it
 * for real — no part of the code path is bypassed and nothing secret crosses the wire. The value is
 * simply predictable, which is what lets the Owner demo check-in live without any screen ever being
 * able to read a genuine candidate's code.
 *
 * Must stay exactly six digits: verifyCandidateOtp rejects anything else before it even reaches the
 * hash comparison, so a "TESTME"-style value would silently make every test org unusable.
 */
export const TEST_ORG_OTP_CODE = "123456";
/**
 * Recruiter-facing label + the exact model instruction each focus injects. Shared so the New
 * Request UI and the question generator never drift on what a focus means.
 */
export const TAILORING_FOCUS_META = {
    probe_gaps: {
        label: "Probe gaps vs. the role",
        guidance: "Where the resume is thin or missing something the role needs, probe that honestly and neutrally.",
    },
    challenge_claims: {
        label: "Challenge claimed impact",
        guidance: "Where the resume claims impact, ownership or scale, ask for the mechanism and the candidate's own specific part in it.",
    },
    recent_project: {
        label: "Go deep on recent work",
        guidance: "Spend real depth on the most recent project or role — decisions, trade-offs, and what they would do differently.",
    },
    career_transitions: {
        label: "Explore career transitions",
        guidance: "Ask about notable role, industry or stack transitions and the reasoning behind them.",
    },
    leadership_scope: {
        label: "Test leadership / scope",
        guidance: "Probe team size, cross-functional influence and how they led or drove outcomes without direct authority.",
    },
    domain_depth: {
        label: "Test domain depth",
        guidance: "Push on domain-specific depth relevant to this role, grounded in concrete artefacts named in the resume.",
    },
    failed_delivery: {
        label: "Probe a delivery that went wrong",
        guidance: "Find a project on the resume that plausibly slipped, was cut, or failed, and probe what THEY owned in it — the early signals, the call they made, and what changed afterwards.",
    },
    commercial_vendor: {
        label: "Test commercial / vendor acumen",
        guidance: "Probe the commercial side of their delivery work — budgets, change requests, vendor underperformance — asking for real numbers and the negotiation they personally ran.",
    },
    production_incidents: {
        label: "Probe production incidents",
        guidance: "Probe real production incidents from their resume's systems — detection, their specific role in the fix, and what they hardened afterwards.",
    },
    system_tradeoffs: {
        label: "Probe design trade-offs",
        guidance: "Push on architecture and design decisions they personally made — the alternative they rejected, why, and how the choice aged in production.",
    },
};
/**
 * Which EXTRA focuses each vertical's Tailoring card shows, on top of the six universal ones.
 * Visibility only — the schema accepts any known focus id, and tailoringGuidanceText renders
 * whatever was stored — so retired extras keep working on old requisitions.
 */
export const VERTICAL_TAILORING_EXTRAS = {
    project_delivery: ["failed_delivery", "commercial_vendor"],
    software_engineering: ["production_incidents", "system_tradeoffs"],
};
/**
 * Render a tailoring policy into a single instruction block appended to the question-generation
 * prompt. Returns "" when the policy is empty, so callers can pass it unconditionally. Pure — the
 * one source of truth for how a policy becomes model guidance (UI preview and real generation
 * both go through here, so what the recruiter previews is what candidates actually get).
 */
export function tailoringGuidanceText(policy) {
    if (!policy)
        return "";
    const lines = [];
    for (const f of policy.focuses ?? []) {
        const meta = TAILORING_FOCUS_META[f];
        if (meta)
            lines.push(`- ${meta.guidance}`);
    }
    const note = (policy.note ?? "").trim();
    if (note)
        lines.push(`- ${note}`);
    if (lines.length === 0)
        return "";
    return ("INTERVIEWER FOCUS (the hiring team asked you to emphasise the following when tailoring to this " +
        "resume — apply where the resume supports it, never fabricate):\n" +
        lines.join("\n"));
}
/**
 * Terminal, human-decided states — a candidate has already been advanced or told an outcome.
 * `apps/api/src/functions/decision.ts` refuses a second decision here without an explicit
 * override; `apps/api/src/functions/adminJudgeCalls.ts` and `apps/worker/src/saga.ts`
 * (rescoreCandidacy) refuse to rescore here at all, at trigger time and again at processing time —
 * once a human has decided, or the candidate has been notified, the report is not something an
 * admin action should quietly change out from under them.
 */
export const DECIDED_CANDIDACY_STATES = new Set([
    "finalist",
    "advanced",
    "cut_notified",
]);
/**
 * A recruiter-editable organizational tag (migration 0077) — deliberately SEPARATE from
 * `CandidacyState`/`DECIDED_CANDIDACY_STATES` above. `state` is system-driven (the saga/API
 * advance it) and is what `decision.ts` gates a real hiring decision on — audited, notifies the
 * candidate on reject, one-way once decided. `pipeline_status` is just a label: freely settable at
 * any time by any org member (`candidacyUpdate` in `apps/api/src/functions/candidacies.ts`), never
 * read by `decision.ts` or `candidacyRescore.ts`, never audited, never notifies anyone. Setting it
 * to "rejected" or "hired" does not reject or hire anyone — it is the recruiter's own tracking
 * label, not the real decision.
 */
export const PIPELINE_STATUSES = [
    "applied",
    "shortlisted",
    "needs_review",
    "on_hold",
    "rejected",
    "hired",
];
export const PIPELINE_STATUS_LABEL = {
    applied: "Applied",
    shortlisted: "Shortlisted",
    needs_review: "Needs review",
    on_hold: "On hold",
    rejected: "Rejected",
    hired: "Hired",
};
/**
 * Where THIS deployment stores candidate data. One cell serves one region; the
 * enum has always allowed `eu-west`, but the recording insert hard-coded
 * `'in-central'`, which meant an EU cell would have been a code change rather
 * than a deployment.
 *
 * It is now read from DATA_REGION, defaulting to in-central (the only cell that
 * exists today). Standing up an EU cell is: deploy the stack to a European
 * region, set DATA_REGION=eu-west, point the org at it. No code edit, no
 * migration. See docs/DATA_RESIDENCY.md for what is and is not offered.
 */
export function configuredDataRegion(env) {
    const raw = (env.DATA_REGION ?? "").trim();
    return raw === "eu-west" || raw === "us-east" || raw === "in-central" ? raw : "in-central";
}
//# sourceMappingURL=types.js.map